'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useT } from '@/components/i18n-provider';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { Settings, X, UserPlus, EyeOff, Upload, Loader2 } from 'lucide-react';
import { coverGradient } from '@/lib/marketplace/cover';
import { cn } from '@/lib/utils';
import { AttachEntityPicker, type AttachedEntity } from '@/components/create/attach-entity-picker';
import { DistributionSection } from '@/components/marketplace/distribution-section';

// Cover swatches (the marketplace category palette) + a Default (deterministic).
const COVER_SWATCHES = ['#6366F1', '#7C3AED', '#2563EB', '#0D9488', '#22C55E', '#F59E0B', '#EF4444', '#EC4899', '#0EA5E9', '#64748B'];

type Mod = {
  userId: string;
  role: string;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

/**
 * Owner/moderator Manage surface for a marketplace listing channel. A non-blocking
 * right-side DRAWER (settings best practice — it doesn't reflow the page) with
 * grouped sections: Details (identity), Moderators (delegation), and an isolated
 * Danger zone (Unpublish). Server-authorised by `canModerateChannel`; rendered
 * only when the viewer can moderate.
 */
export function ManageApp({
  channelId,
  slug,
  name: initialName,
  description: initialDescription,
  color: initialColor,
  bannerUrl: initialBannerUrl,
  logoUrl: initialLogoUrl,
  root,
  primaryContentId: initialPrimaryContentId = null,
  primaryAgentId: initialPrimaryAgentId = null,
}: {
  channelId: string;
  slug: string;
  name: string;
  description: string;
  color?: string | null;
  bannerUrl?: string | null;
  logoUrl?: string | null;
  root: 'apps' | 'agents';
  /** Current binding — used by the community-channel attach section. */
  primaryContentId?: string | null;
  primaryAgentId?: string | null;
}) {
  const t = useT();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [color, setColor] = useState<string | null>(initialColor ?? null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(initialBannerUrl ?? null);
  const [logoUrl, setLogoUrl] = useState<string | null>(initialLogoUrl ?? null);
  const [uploading, setUploading] = useState<null | 'cover' | 'logo'>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mods, setMods] = useState<Mod[]>([]);
  const [newMod, setNewMod] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [availableCats, setAvailableCats] = useState<{ id: string; name: string }[]>([]);
  const [listed, setListed] = useState(true);
  const [listedSupported, setListedSupported] = useState(false);
  const [confirmingUnpublish, setConfirmingUnpublish] = useState(false);
  // Attached app/agent — community channels only (marketplace listing
  // bindings are pipeline-managed; the API rejects edits there).
  const isMarketplaceListing = /^(apps|agents)\//.test(slug);
  const [attached, setAttached] = useState<AttachedEntity | null>(
    initialPrimaryContentId
      ? { kind: 'app', id: initialPrimaryContentId, name: 'Attached app' }
      : initialPrimaryAgentId
        ? { kind: 'agent', id: initialPrimaryAgentId, name: 'Attached agent' }
        : null,
  );

  const saveAttachment = async (next: AttachedEntity | null) => {
    const prev = attached;
    setAttached(next); // optimistic
    setError(null);
    try {
      const res = await fetch(`/api/channels/${slug}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          primary_content_id: next?.kind === 'app' ? next.id : null,
          primary_agent_id: next?.kind === 'agent' ? next.id : null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || `Failed (${res.status})`);
      router.refresh();
    } catch (e) {
      setAttached(prev); // revert
      setError((e as Error).message);
    }
  };

  const noun = root === 'agents' ? 'agent' : 'app';

  const loadMods = useCallback(async () => {
    try {
      const res = await fetch(`/api/channels/${slug}/moderators`);
      if (res.ok) setMods(((await res.json())?.moderators as Mod[]) ?? []);
    } catch {
      /* ignore */
    }
  }, [slug]);

  const loadCategories = useCallback(async () => {
    try {
      const [cur, all] = await Promise.all([
        fetch(`/api/channels/${slug}/category`).then((r) => (r.ok ? r.json() : null)),
        fetch(`/api/marketplace/channels?root=${root}`).then((r) => (r.ok ? r.json() : null)),
      ]);
      if (cur?.categories) setCategories(cur.categories as string[]);
      if (cur && typeof cur.listed === 'boolean') setListed(cur.listed);
      if (cur && typeof cur.listedSupported === 'boolean') setListedSupported(cur.listedSupported);
      if (all?.categories) {
        setAvailableCats(
          (all.categories as { slug: string; name: string }[]).map((c) => ({
            id: c.slug.slice(root.length + 1),
            name: c.name,
          })),
        );
      }
    } catch {
      /* ignore */
    }
  }, [slug, root]);

  useEffect(() => {
    if (open) {
      void loadMods();
      void loadCategories();
    }
  }, [open, loadMods, loadCategories]);

  // Categories save instantly on toggle (same model as the visibility switch);
  // no separate Save button.
  const toggleCategory = async (id: string) => {
    const prev = categories;
    const next = prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id];
    setCategories(next); // optimistic
    setError(null);
    try {
      const res = await fetch(`/api/channels/${slug}/category`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ categories: next }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || `Failed (${res.status})`);
      router.refresh();
    } catch (e) {
      setCategories(prev); // revert
      setError((e as Error).message);
    }
  };

  const toggleListed = async () => {
    const next = !listed;
    setListed(next); // optimistic
    setError(null);
    try {
      const res = await fetch(`/api/channels/${slug}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ listed: next }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || `Failed (${res.status})`);
      router.refresh();
    } catch (e) {
      setListed(!next); // revert
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy]);

  // Upload a cover/thumbnail image via the shared /api/upload endpoint (same
  // path settings uses for avatars). Returns the stored URL; null on failure.
  const uploadImage = async (file: File, kind: 'cover' | 'logo') => {
    if (!file.type.startsWith('image/')) { setError('Please choose an image file (PNG, JPG, GIF, WebP).'); return; }
    if (file.size > 20 * 1024 * 1024) { setError('Image must be under 20MB.'); return; }
    setUploading(kind);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'post');
      const res = await fetch('/api/upload', { method: 'POST', body: fd });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.url) throw new Error(data?.error || `Upload failed (${res.status})`);
      if (kind === 'cover') setBannerUrl(data.url as string);
      else setLogoUrl(data.url as string);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/channels/${slug}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name: name.trim() || initialName, description, color, bannerUrl, logoUrl }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || `Save failed (${res.status})`);
      router.refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const unpublish = async () => {
    setBusy(true);
    setError(null);
    try {
      // Marketplace listing → unpublish (marketplace route, preserves the
      // entity). Community channel → delete the channel itself (posts are
      // detached server-side, not destroyed).
      const res = isMarketplaceListing
        ? await fetch(`/api/marketplace/channels?id=${channelId}`, { method: 'DELETE' })
        : await fetch(`/api/channels/${slug}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || `Failed (${res.status})`);
      router.push(isMarketplaceListing ? `/${root === 'apps' ? 'app' : 'agent'}` : '/workspace');
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const addMod = async () => {
    const handle = newMod.trim().replace(/^@/, '');
    if (!handle) return;
    setError(null);
    const res = await fetch(`/api/channels/${slug}/moderators`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username: handle, role: 'moderator' }),
    });
    if (!res.ok) {
      setError((await res.json().catch(() => null))?.error || 'Could not add moderator');
      return;
    }
    setNewMod('');
    void loadMods();
  };

  const removeMod = async (userId: string) => {
    await fetch(`/api/channels/${slug}/moderators?userId=${userId}`, { method: 'DELETE' });
    void loadMods();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
      >
        <Settings className="h-4 w-4" />
        Manage
      </button>

      {open && typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[200]">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px]" onClick={() => !busy && setOpen(false)} />
            <div className="absolute right-0 top-0 bottom-0 flex w-[400px] max-w-[94vw] flex-col border-l border-border/60 bg-background shadow-2xl">
              {/* Header */}
              <div className="flex shrink-0 items-center gap-2 border-b border-border/60 px-4 py-3">
                <Settings className="h-4 w-4 text-muted-foreground" />
                <span className="truncate text-sm font-semibold">Manage {initialName}</span>
                <button
                  type="button"
                  aria-label={t('workspace.canvas.node.close')}
                  onClick={() => !busy && setOpen(false)}
                  className="ml-auto rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {/* Details */}
                <SectionLabel>Details</SectionLabel>
                <label className="mb-1 block text-[11px] text-muted-foreground">Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mb-3 w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
                />
                <label className="mb-1 block text-[11px] text-muted-foreground">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="mb-3 w-full resize-none rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
                />
                <label className="mb-1 block text-[11px] text-muted-foreground">Cover</label>
                <div
                  className="mb-2 h-16 w-full overflow-hidden rounded-lg"
                  style={{ background: bannerUrl ? undefined : coverGradient(channelId, color).css }}
                >
                  {bannerUrl && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={bannerUrl} alt="" className="h-full w-full object-cover" />
                  )}
                </div>
                <div className="mb-3 flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => coverInputRef.current?.click()}
                    disabled={uploading === 'cover'}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2.5 py-1 text-[11px] font-medium transition-colors hover:bg-accent disabled:opacity-50"
                  >
                    {uploading === 'cover' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    {bannerUrl ? t('marketplace.replace') : t('marketplace.uploadImage')}
                  </button>
                  {bannerUrl ? (
                    <button
                      type="button"
                      onClick={() => setBannerUrl(null)}
                      className="rounded-md px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                    >
                      Remove
                    </button>
                  ) : (
                    <>
                      <span className="mx-0.5 h-4 w-px bg-border/60" />
                      {COVER_SWATCHES.map((c) => (
                        <button
                          key={c}
                          type="button"
                          aria-label={`Cover ${c}`}
                          onClick={() => setColor(c)}
                          className={cn(
                            'h-6 w-6 rounded-full border transition-transform hover:scale-110',
                            color === c ? 'border-foreground ring-2 ring-foreground/30' : 'border-border/40',
                          )}
                          style={{ background: c }}
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => setColor(null)}
                        className={cn(
                          'rounded-full border px-2.5 py-1 text-[11px] transition-colors hover:bg-accent',
                          color === null ? 'border-foreground' : 'border-border/40 text-muted-foreground',
                        )}
                      >
                        Auto
                      </button>
                    </>
                  )}
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadImage(f, 'cover'); e.target.value = ''; }}
                  />
                </div>

                <label className="mb-1 block text-[11px] text-muted-foreground">Thumbnail</label>
                <div className="mb-3 flex items-center gap-2">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border/40"
                    style={{ background: logoUrl ? undefined : coverGradient(channelId, color).css }}
                  >
                    {logoUrl && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={logoUrl} alt="" className="h-full w-full object-cover" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading === 'logo'}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2.5 py-1 text-[11px] font-medium transition-colors hover:bg-accent disabled:opacity-50"
                  >
                    {uploading === 'logo' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    {logoUrl ? 'Replace' : 'Upload'}
                  </button>
                  {logoUrl && (
                    <button
                      type="button"
                      onClick={() => setLogoUrl(null)}
                      className="rounded-md px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                    >
                      Remove
                    </button>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadImage(f, 'logo'); e.target.value = ''; }}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => void save()}
                  disabled={busy}
                  className="w-full rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
                >
                  {busy ? 'Saving…' : 'Save details'}
                </button>

                {/* Attached app/agent — community channels only. Changing it
                    re-points the channel page's header + Open/Chat CTA. */}
                {!isMarketplaceListing && (
                  <div className="mt-6">
                    <SectionLabel>{t('marketplace.attachedAppOrAgent')}</SectionLabel>
                    <AttachEntityPicker value={attached} onChange={(v) => void saveAttachment(v)} />
                    <p className="mt-1.5 text-[11px] text-muted-foreground">
                      The channel page shows this app or agent&apos;s header and its Open / Chat button.
                    </p>
                  </div>
                )}

                {/* Moderators */}
                <div className="mt-6">
                  <SectionLabel>Moderators</SectionLabel>
                  <div className="mb-2 flex flex-col">
                    {mods.length === 0 && (
                      <p className="py-1 text-[12px] text-muted-foreground">{t('marketplace.noModerators')}</p>
                    )}
                    {mods.map((m) => (
                      <div key={m.userId} className="flex items-center gap-2 py-1 text-sm">
                        {m.avatarUrl ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={m.avatarUrl} alt="" className="h-6 w-6 rounded-full object-cover" />
                        ) : (
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-medium uppercase">
                            {(m.username ?? '?').slice(0, 2)}
                          </span>
                        )}
                        <span className="min-w-0 flex-1 truncate">
                          {m.displayName || `@${m.username}`}
                          <span className="ml-1.5 text-[11px] text-muted-foreground">{m.role}</span>
                        </span>
                        {m.role !== 'owner' && (
                          <button
                            type="button"
                            aria-label={`Remove @${m.username}`}
                            onClick={() => void removeMod(m.userId)}
                            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      value={newMod}
                      onChange={(e) => setNewMod(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void addMod(); } }}
                      placeholder={t('marketplace.usernamePlaceholder')}
                      className="flex-1 rounded-lg border border-border/60 bg-background px-3 py-1.5 text-sm outline-none focus:border-primary/60"
                    />
                    <button
                      type="button"
                      onClick={() => void addMod()}
                      className="inline-flex items-center gap-1 rounded-lg border border-border/60 px-3 py-1.5 text-sm font-medium hover:bg-accent"
                    >
                      <UserPlus className="h-4 w-4" />
                      Add
                    </button>
                  </div>
                </div>

                {/* Categories (tags) — mutable; the channel slug stays put. */}
                <div className="mt-6">
                  <SectionLabel>Categories</SectionLabel>
                  <p className="mb-2 text-[11px] text-muted-foreground">
                    Where this {noun} shows up in Explore. You can pick more than one.
                  </p>
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {availableCats.map((c) => {
                      const on = categories.includes(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleCategory(c.id)}
                          className={cn(
                            'rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors',
                            on
                              ? 'border-foreground bg-foreground text-background'
                              : 'border-border/60 text-muted-foreground hover:bg-accent',
                          )}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                    {availableCats.length === 0 && (
                      <span className="text-[12px] text-muted-foreground">{t('marketplace.noCategories')}</span>
                    )}
                  </div>
                </div>

                {/* Visibility (listed vs unlisted). Hidden until migration 0041
                    adds the column (listedSupported), so it never 400s. */}
                {listedSupported && (
                <div className="mt-6">
                  <SectionLabel>Visibility</SectionLabel>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={listed}
                      onClick={() => void toggleListed()}
                      className={cn(
                        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none',
                        listed ? 'bg-emerald-500' : 'bg-foreground/20',
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform',
                          listed ? 'translate-x-5' : 'translate-x-0',
                        )}
                      />
                    </button>
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium">
                        {listed ? t('marketplace.listed') : t('workspace.panels.unlisted')}
                      </div>
                      <div className="text-[11px] leading-snug text-muted-foreground">
                        {listed
                          ? `Shows in Explore and search.`
                          : `Hidden from browse; still reachable by direct link.`}
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Distribution (app-hosts plan G1): the app's own address,
                    how it installs, and its Android packages. Apps only; an
                    agent listing has no host. */}
                {isMarketplaceListing && root === 'apps' && attached?.kind === 'app' && (
                  <DistributionSection contentId={attached.id} />
                )}

                {error && <p className="mt-4 text-xs text-red-500">{error}</p>}

                {/* Danger zone — Unpublish for marketplace listings (entity
                    kept, relistable); Delete for community channels (public
                    posts stay with their authors, private ones go, S-631). */}
                <div className="mt-6 rounded-lg border border-red-500/40 p-3">
                  <div className="text-[13px] font-medium text-red-500">{t('marketplace.dangerZone')}</div>
                  <p className="mb-3 mt-0.5 text-[12px] leading-snug text-muted-foreground">
                    {isMarketplaceListing ? (
                      <>{t('marketplace.unpublishRemovesFrom')} <span className="font-mono">//{root}</span>. The {noun} itself is kept.</>
                    ) : (
                      <>{t('marketplace.deleteChannelNote')}</>
                    )}
                  </p>
                  {confirmingUnpublish ? (
                    <div className="flex flex-col gap-2">
                      <p className="text-[12px] font-medium text-red-500">
                        {isMarketplaceListing
                          ? `Unpublish this ${noun}? You can re-publish it later.`
                          : 'Delete this channel? This cannot be undone.'}
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => void unpublish()}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-red-500 px-3 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
                        >
                          <EyeOff className="h-4 w-4" />
                          {busy
                            ? isMarketplaceListing ? t('marketplace.unpublishing') : t('agentTools.deleting')
                            : isMarketplaceListing ? t('marketplace.yesUnpublish') : t('marketplace.yesDelete')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmingUnpublish(false)}
                          disabled={busy}
                          className="rounded-lg border border-border/60 px-3 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmingUnpublish(true)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/50 px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                    >
                      <EyeOff className="h-4 w-4" />
                      {isMarketplaceListing ? t('marketplace.unpublish') : t('marketplace.deleteChannel')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
